import { useCallback, useEffect, useState } from 'react';

import type { ConversionJob } from '../store/hooks';
import {
  useLaunchMigrationConversionMutation,
  useLookupMigrationConversionsMutation,
} from '../store/hooks';

/** Poll durable worker state in bounded batches, discarding responses after scope changes. */
export function useMigrationConversions(organizationId: string, rawBatchIds: string[]) {
  const [lookup] = useLookupMigrationConversionsMutation();
  const [launch] = useLaunchMigrationConversionMutation();
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<{ scope: string; jobs: ConversionJob[]; known: boolean; enabled: boolean }>({
    scope: '',
    enabled: false,
    jobs: [],
    known: false,
  });
  const [launchingSourceId, setLaunchingSourceId] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const sourceKey = [...new Set(rawBatchIds)].sort().join(',');
  const scope = `${organizationId}:${sourceKey}`;
  const refresh = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const ids = sourceKey ? sourceKey.split(',') : [];
    const poll = async () => {
      const jobs: ConversionJob[] = [];
      let known = true;
      let enabled = true;
      try {
        for (let offset = 0; offset < ids.length && !cancelled; offset += 100) {
          const response = await lookup({
            organizationId,
            conversionLookupRequest: { raw_batch_ids: ids.slice(offset, offset + 100) },
          }).unwrap();
          enabled = enabled && response.enabled === true;
          jobs.push(...response.jobs);
        }
      } catch {
        known = false;
      }
      if (cancelled) return;
      setState(previous => ({
        scope,
        jobs: known ? jobs : previous.scope === scope ? previous.jobs : [],
        known,
        enabled,
      }));
      const active = jobs.some(job => job.status === 'pending' || job.status === 'running');
      if (ids.length > 0) timer = setTimeout(() => void poll(), active || !known ? 5000 : 30000);
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [organizationId, sourceKey, scope, version, lookup]);

  const go = async (rawBatchId: string, retryOf?: string) => {
    setLaunchingSourceId(rawBatchId);
    setLaunchError(null);
    try {
      const { job } = await launch({
        organizationId,
        rawBatchId,
        conversionLaunchRequest: retryOf ? { retry_of: retryOf } : {},
      }).unwrap();
      setState(previous => ({
        scope,
        enabled: previous.scope === scope && previous.enabled,
        known: previous.scope === scope && previous.known,
        jobs: [
          ...(previous.scope === scope
            ? previous.jobs.filter(value => value.raw_batch_id !== rawBatchId)
            : []),
          job,
        ],
      }));
    } catch {
      setState(previous => ({ ...previous, known: false }));
      setLaunchError(
        'We could not confirm the conversion request. Refresh to check its status before trying again.',
      );
    } finally {
      setLaunchingSourceId(null);
      refresh();
    }
  };
  return {
    jobs: state.scope === scope ? state.jobs : [],
    enabled: state.scope === scope && state.enabled,
    known: state.scope === scope && state.known,
    launchingSourceId,
    launchError,
    go,
    refresh,
  };
}
